import { RefObject, useCallback, useEffect, useRef, useState } from 'react'

import useFocus from '~/bootcamp/Activities/hooks/useFocus'
import { getRandomInt } from '~/bootcamp/Activities/Fractions/Fractions.utils'

const VARIABLES: string[][] = [
  ['a', 'b', 'c', 'x', 'y', 'z'],
  ['ab', 'bc', 'ac', 'ax', 'by', 'cz', 'xy', 'yz'],
]

const MAX_HISTORY_LENGTH = [3, 4]

interface UseCoefficientOneReturnType {
  variable: string
  formValid: boolean
  answer: string
  handleCheckAnswer: () => void
  onInputChange: (val?: string) => void
  goToNextQuestion: () => void
  inputRef: RefObject<HTMLInputElement>
}

interface UseCoefficientOneProps {
  difficultyLevel: number
  onNextQuestionClick: () => void
  onAnswerStateChange: (value: boolean | null) => void
  autofocus: boolean
  answerState: boolean | null
}

function UseCoefficientOne({
  onAnswerStateChange,
  onNextQuestionClick,
  difficultyLevel,
  autofocus,
  answerState,
}: UseCoefficientOneProps): UseCoefficientOneReturnType {
  const [taskHistory, setTaskHistory] = useState<string[]>([])
  const [variable, setVariable] = useState('')
  const [answer, setAnswer] = useState('')

  const inputRef = useRef<HTMLInputElement>(null)
  useFocus(autofocus, answerState, inputRef)

  useEffect(() => {
    setAnswer('')
    let _variable = VARIABLES[difficultyLevel - 1][getRandomInt(0, VARIABLES[difficultyLevel - 1].length - 1)]
    // eslint-disable-next-line
    while (taskHistory.includes(_variable)) {
      _variable = VARIABLES[difficultyLevel - 1][getRandomInt(0, VARIABLES[difficultyLevel - 1].length - 1)]
    }
    setVariable(_variable)
  }, [taskHistory, difficultyLevel])

  const handleCheckAnswer = useCallback((): void => {
    let isCorrect = true
    let _answer = answer.replaceAll(' ', '')
    if (variable.length < 2) {
      if (variable !== _answer) {
        isCorrect = false
      }
    } else {
      if (_answer.length === 3) {
        if (_answer[1] !== '*') {
          isCorrect = false
        }
      } else if (_answer.length > 3) {
        isCorrect = false
      }
      _answer = _answer.replaceAll('*', '')
      // eslint-disable-next-line fp/no-mutating-methods
      _answer = _answer
        .split('')
        .sort((a, b) => a.charCodeAt(0) - b.charCodeAt(0))
        .join('')
      if (_answer !== variable) {
        isCorrect = false
      }
    }
    onAnswerStateChange(isCorrect)
  }, [onAnswerStateChange, answer, variable])

  const onInputChange = useCallback((value: string) => {
    setAnswer(value)
  }, [])

  const goToNextQuestion = useCallback(() => {
    setTaskHistory((prevState) => [variable, ...prevState].slice(0, MAX_HISTORY_LENGTH[difficultyLevel - 1]))
    onNextQuestionClick()
  }, [onNextQuestionClick, variable, difficultyLevel])

  return {
    variable,
    answer,
    formValid: answer !== '',
    handleCheckAnswer,
    onInputChange,
    goToNextQuestion,
    inputRef,
  }
}

export default UseCoefficientOne
